<template>
  <div class="OrgMemberSiteAccessList">
    <div class="OrgMemberSiteAccessList__title mb-2">Site access</div>

    <div v-if="loading" class="OrgMemberSiteAccessList__status">
      Loading sites...
    </div>
    <div v-else-if="error" class="OrgMemberSiteAccessList__error pa-2 mb-2">
      {{ error }}
    </div>
    <div
      v-else-if="!memberships.length"
      class="OrgMemberSiteAccessList__status"
    >
      {{ projectTerms.noSitesInContainer }}
    </div>
    <div v-else class="OrgMemberSiteAccessList__list">
      <div
        v-for="membership in memberships"
        :key="membership.site_id"
        class="OrgMemberSiteAccessList__row"
      >
        <span class="OrgMemberSiteAccessList__site-name">
          {{ membership.site_name }}
        </span>
        <v-switch
          :input-value="switchValue(membership)"
          :disabled="isRowSaving(membership.site_id)"
          dense
          hide-details
          class="OrgMemberSiteAccessList__toggle"
          @change="onToggle(membership, $event)"
        />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'OrgMemberSiteAccessList',
  props: {
    organisationUserId: {
      type: [Number, String],
      required: true,
    },
    userId: {
      type: [Number, String],
      required: true,
    },
    projectId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      error: '',
      memberships: [],
      savingSiteId: null,
      enablingSiteId: null,
    }
  },
  computed: {
    projectTerms() {
      return this.$store.getters['organisations/projectTerms']
    },
  },
  watch: {
    projectId: {
      immediate: true,
      handler() {
        this.loadMemberships()
      },
    },
    userId() {
      this.loadMemberships()
    },
  },
  methods: {
    hasAccess(membership) {
      return membership.site_user_id != null
    },
    isEnablingSite(siteId) {
      return this.enablingSiteId === siteId
    },
    switchValue(membership) {
      return (
        this.hasAccess(membership) || this.isEnablingSite(membership.site_id)
      )
    },
    isRowSaving(siteId) {
      return this.savingSiteId === siteId
    },
    extractSiteUser(res) {
      const body = res?.body
      if (!body) return null
      if (body.site_user) return body.site_user
      if (body.id && body.site_id) return body
      return null
    },
    applySiteUser(membership, siteUser) {
      if (!siteUser?.id) return false

      this.$set(membership, 'site_user_id', siteUser.id)
      return true
    },
    async syncMembershipRow(siteId) {
      const latest = await this.$store.dispatch(
        'organisation_user/loadSiteMemberships',
        {
          organisationUserId: this.organisationUserId,
          projectId: this.projectId,
          userId: this.userId,
        }
      )
      const updated = latest.find((row) => row.site_id === siteId)
      const index = this.memberships.findIndex((row) => row.site_id === siteId)

      if (updated && index !== -1) {
        this.$set(this.memberships, index, updated)
      }
    },
    async loadMemberships() {
      if (!this.projectId || !this.userId || !this.organisationUserId) {
        if (!this.organisationUserId) {
          this.error =
            'This user must be an organisation member before site access can be managed.'
        }
        this.loading = false
        return
      }

      this.loading = true
      this.error = ''

      try {
        this.memberships = await this.$store.dispatch(
          'organisation_user/loadSiteMemberships',
          {
            organisationUserId: this.organisationUserId,
            projectId: this.projectId,
            userId: this.userId,
          }
        )
      } catch (error) {
        this.error =
          error?.body?.message ||
          error?.message ||
          'Failed to load site access.'
      } finally {
        this.loading = false
      }
    },
    async onToggle(membership, enabled) {
      if (this.isRowSaving(membership.site_id)) return

      this.savingSiteId = membership.site_id
      this.error = ''

      if (enabled) {
        this.enablingSiteId = membership.site_id
      } else {
        this.enablingSiteId = null
      }

      try {
        if (enabled) {
          const res = await this.$store.dispatch('site_user/addUser', {
            user_id: this.userId,
            site_id: membership.site_id,
          })

          const siteUser = this.extractSiteUser(res)
          if (!this.applySiteUser(membership, siteUser)) {
            await this.syncMembershipRow(membership.site_id)
          }
        } else if (membership.site_user_id) {
          await this.$store.dispatch(
            'site_user/removeMembership',
            membership.site_user_id
          )
          this.$set(membership, 'site_user_id', null)
        }
      } catch (error) {
        this.error =
          error?.body?.message ||
          error?.message ||
          `Failed to update access for ${membership.site_name}.`
        await this.syncMembershipRow(membership.site_id)
      } finally {
        this.savingSiteId = null
        this.enablingSiteId = null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.OrgMemberSiteAccessList {
  padding: 8px 0 0 16px;
  border-left: 2px solid rgba(94, 99, 109, 0.35);

  &__title {
    color: $N3;
    font-size: 12px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  &__status {
    color: $N3;
    font-size: 13px;
  }

  &__error {
    background: rgba(233, 74, 83, 0.15);
    border: 1px solid rgba(233, 74, 83, 0.35);
    border-radius: 8px;
    color: #ffb4b9;
    font-size: 13px;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 72px;
    gap: 12px;
    align-items: center;
    padding: 4px 0;
  }

  &__site-name {
    color: $N2;
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__toggle {
    justify-self: center;
    margin-top: 0;
  }
}
</style>
